// ============================================================
// M365LifecycleCard.js — the Platform-03 per-client lifecycle config
// (2026-08-14). Until this card, flipping LifecycleEnabled or tuning
// the offboarding clocks was a manual SQL edit; Phase 3 depends on it.
//
// Absence of a row == lifecycle DISABLED (the chokepoint's fail-closed
// contract) — the card says so and saving creates the row. The master
// kill switch (M365_LIFECYCLE_ENABLED) and tenant allowlist stay
// deliberate main.bicep commits (D3) and are NOT edited here.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import Field from '../../components/Field';
import Switch from '../../components/Switch';
import { toast } from '../../components/Toaster';
import { getClientM365LifecycleConfig, patchClientM365LifecycleConfig } from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

export default function M365LifecycleCard({ clientId }) {
  const [enabled, setEnabled] = useState(false);
  const [upnConvention, setUpnConvention] = useState('');
  const [licenseSku, setLicenseSku] = useState('');
  const [graceWeeks, setGraceWeeks] = useState(4);
  const [delayWeeks, setDelayWeeks] = useState(2);
  const [conventions, setConventions] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientM365LifecycleConfig(clientId);
      setEnabled(!!res.lifecycleEnabled);
      setUpnConvention(res.upnConvention || '');
      setLicenseSku(res.licenseSkuPartNumber || '');
      setGraceWeeks(res.disabledGraceWeeks);
      setDelayWeeks(res.deletionDelayWeeks);
      setConventions(res.upnConventions || []);
      setIsDefault(!!res.isDefault);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await patchClientM365LifecycleConfig(clientId, {
        lifecycleEnabled: enabled,
        upnConvention: upnConvention || null,
        licenseSkuPartNumber: licenseSku.trim() || null,
        disabledGraceWeeks: Number(graceWeeks),
        deletionDelayWeeks: Number(delayWeeks),
      });
      toast.success('M365 lifecycle config saved');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setSaving(false); }
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  return (
    <form className="pc-form" onSubmit={submit}>
      {isDefault && (
        <StatusMessage as="p" variant="empty" className="pc-state">
          No config row yet — the lifecycle is DISABLED for this client (fail-closed). Saving creates the row.
        </StatusMessage>
      )}
      <Field
        label="Lifecycle enabled"
        help="The per-client capability gate. Off = the lifecycle chokepoint refuses every operation for this client regardless of consent/allowlist state. The MASTER kill switch and tenant allowlist stay main.bicep commits and are not edited here."
      >
        <Switch checked={enabled} onChange={setEnabled} ariaLabel="M365 lifecycle enabled" />
      </Field>
      <Field
        label="UPN convention"
        help="For NEW provisions. Set deliberately after the sampler reports what the tenant's estate actually uses — blank uses the platform default (first.last)."
      >
        <select value={upnConvention} onChange={(e) => setUpnConvention(e.target.value)} data-testid="m365-upn-convention">
          <option value="">Platform default (first.last)</option>
          {conventions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field
        label="Licence pool SKU (skuPartNumber)"
        help="The Graph subscribedSkus skuPartNumber for the pool (blank uses the platform default M365_F1_COMM — verify against the live tenant's subscribedSkus; the marketing name hides suffixes like _COMM, Gotcha #135)."
      >
        <input type="text" maxLength={50} value={licenseSku} onChange={(e) => setLicenseSku(e.target.value)} data-testid="m365-license-sku" />
      </Field>
      <Field label="Disabled grace (weeks)" required help="How long a disabled account rests before deletion eligibility starts (1-52; default 4).">
        <input type="number" min={1} max={52} value={graceWeeks} onChange={(e) => setGraceWeeks(e.target.value)} data-testid="m365-grace-weeks" />
      </Field>
      <Field label="Deletion delay (weeks)" required help="Added on top of the grace before deletion becomes eligible; re-verified at fire time (1-52; default 2).">
        <input type="number" min={1} max={52} value={delayWeeks} onChange={(e) => setDelayWeeks(e.target.value)} data-testid="m365-delay-weeks" />
      </Field>
      <div className="pc-actions">
        <button type="submit" className="pc-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
